import { createContext, useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { translations, type TranslationKeys } from '@/i18n/translations';

export type Lang = 'en' | 'ar' | 'fr';

interface LanguageContextType {
  lang: Lang;
  t: TranslationKeys;
  dir: 'ltr' | 'rtl';
  switchLanguage: (newLang: Lang) => void;
}

const defaultValue: LanguageContextType = {
  lang: 'en',
  t: translations.en,
  dir: 'ltr',
  switchLanguage: () => {},
};

const LanguageContext = createContext<LanguageContextType>(defaultValue);

export const useLanguage = () => useContext(LanguageContext);

export const LanguageProvider = ({ lang, children }: { lang: Lang; children: React.ReactNode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dir = lang === 'ar' ? 'rtl' : 'ltr';

  useEffect(() => {
    document.documentElement.dir = dir;
    document.documentElement.lang = lang;
    if (lang === 'ar') {
      document.documentElement.style.fontFamily = '"Zain", "Montserrat", sans-serif';
    } else {
      document.documentElement.style.fontFamily = '';
    }
  }, [dir, lang]);

  const switchLanguage = (newLang: Lang) => {
    const newPath = location.pathname.replace(`/${lang}`, `/${newLang}`);
    if (newPath === location.pathname || newPath === '') {
      navigate(`/${newLang}`);
    } else {
      navigate(newPath);
    }
  };

  // French uses the same translations structure as English
  const t = lang === 'fr' ? translations.fr : translations[lang];

  return (
    <LanguageContext.Provider value={{ lang, t, dir, switchLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};
